// v50.213 — Shared notification routing helper.
//
// Both /notifications (centre) and the topbar bell need to land users
// on the right area when a notification is clicked. Brief §6 specifies
// each domain bucket's home area; this helper encodes the canonical
// mapping in one place so the two surfaces stay in sync.
//
// Returns a URL string (pathname + query). Callers use react-router's
// navigate(target).

import type { NotificationKind, PlacementNotification } from "@/lib/api/types";

interface RoutableNotification extends Pick<PlacementNotification, "kind" | "source_id" | "payload"> {
  placement?: { id?: string } | null;
}

export function routeFor(n: RoutableNotification): string {
  /* Disputes — Payments ▸ Disputes per brief table. */
  if (n.kind === "dispute_filed" || n.kind === "dispute_updated") return "/disputes";

  /* Money events land on the deal's invoice tab when a placement is
     known, otherwise the Payments surface. */
  if (n.kind === "invoice_sent" || n.kind === "invoice_paid") {
    return n.placement?.id ? `/deals/${n.placement.id}?tab=invoices` : "/payments";
  }
  if (n.kind === "invoice_aging") return "/payments";

  /* Milestone events stay on the deal's milestones tab. The brief puts
     milestone_evidence_submitted in disputes, but the source surface is
     still the deal — only the bucket grouping differs. */
  if (
    n.kind === "milestone_evidence_submitted" ||
    n.kind === "milestone_marked_met" ||
    n.kind === "milestone_deemed_met"
  ) {
    return n.placement?.id ? `/deals/${n.placement.id}?tab=milestones` : "/deals";
  }

  /* Bid engine — route to the tender. source_id or payload.opportunity_id. */
  if (
    n.kind === "bid_opportunity_matched" ||
    n.kind === "tender_amendment_published" ||
    n.kind === "tender_clarification_raised" ||
    n.kind === "tender_clarification_answered" ||
    n.kind === "bid_pack_submitted" ||
    n.kind === "bid_outcome_shortlisted" ||
    n.kind === "bid_outcome_won" ||
    n.kind === "bid_outcome_lost" ||
    n.kind === "tender_invitation_received" ||
    n.kind === "tender_invitation_reminder"
  ) {
    const oppId = (n.payload?.opportunity_id as string | undefined) || n.source_id || null;
    return oppId ? `/tenders/${oppId}` : "/tenders";
  }

  /* Futures booking lifecycle — /futures with confirm param. */
  if (
    n.kind === "futures_booking_received" ||
    n.kind === "futures_booking_confirmed" ||
    n.kind === "futures_booking_declined"
  ) {
    const bookingId = (n.payload?.booking_id as string | undefined) || n.source_id || null;
    return bookingId ? `/futures?confirm=${bookingId}` : "/futures";
  }

  /* Partner offers — /futures for everything except accepted which
     creates a placement reachable in /deals. */
  if (n.kind === "partner_offer_accepted") return "/deals";
  if (
    n.kind === "partner_offer_received" ||
    n.kind === "partner_offer_declined" ||
    n.kind === "partner_offer_withdrawn"
  ) {
    return "/futures";
  }

  /* v50.328 — Defence P2: framework agreement lifecycle → My Frameworks. */
  if (
    n.kind === "framework_agreement_received" ||
    n.kind === "framework_agreement_signed" ||
    n.kind === "framework_agreement_declined" ||
    n.kind === "framework_agreement_terminated" ||
    n.kind === "framework_agreement_renewal_due"
  ) {
    return "/frameworks";
  }

  /* Compliance — straight into the wallet. */
  if (
    n.kind === "compliance_requested" ||
    n.kind === "compliance_fulfilled" ||
    n.kind === "compliance_expiring"
  ) {
    return "/compliance";
  }

  /* Contract offers — the deal profile if we have a placement. */
  if (n.kind === "contract_offer_sent" || n.kind === "contract_offer_responded") {
    return n.placement?.id ? `/deals/${n.placement.id}?tab=contract` : "/deals";
  }

  /* Stage change — the deal profile. */
  if (n.kind === "stage_change") {
    return n.placement?.id ? `/deals/${n.placement.id}` : "/deals";
  }

  /* v50.351 Retention B10 — on mobile viewports, route the three
     critical action notifications to the bare /m/* screens designed for
     ~30s thumb attention. Desktop users keep the full deal-profile
     surfaces. The B1 push pump also writes url_path into push_outbox at
     send time so service-worker pushes go to /m/* on mobile devices
     regardless of who's currently looking at the app. */
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  /* Logs — deal profile logs tab. */
  if (n.kind === "log_submitted" || n.kind === "log_confirmed" || n.kind === "log_unconfirmed_aging") {
    if (isMobile && n.kind === "log_submitted" && n.payload?.log_id) {
      return `/m/log/${n.payload.log_id as string}`;
    }
    return n.placement?.id ? `/deals/${n.placement.id}?tab=logs` : "/deals";
  }

  /* Proposals — coord-side review surface. v50.211 added the
     proposal_received kind for the initial INSERT. */
  if (n.kind === "proposal_received") {
    if (isMobile && n.placement?.id) return `/m/proposal/${n.placement.id}`;
    return n.placement?.id ? `/deals/${n.placement.id}` : "/deals";
  }
  if (n.kind === "proposal_aging") {
    return n.placement?.id ? `/deals/${n.placement.id}` : "/deals";
  }

  /* Messages — inbox. */
  if (n.kind === "message") {
    return n.placement?.id ? `/deals/${n.placement.id}?tab=messages` : "/inbox";
  }

  /* Ratings — the operative profile if we can find them; otherwise deals. */
  if (n.kind === "rating") {
    return n.placement?.id ? `/deals/${n.placement.id}?tab=ratings` : "/deals";
  }

  /* v50.330 — Defence P4: rating_due deep-links into the deal with the
     rate modal auto-opened. */
  if (n.kind === "rating_due") {
    return n.placement?.id ? `/deals/${n.placement.id}?rate=1` : "/deals";
  }

  /* Fallback. */
  return "/notifications";
}

export function bucketFor(kind: NotificationKind): "money" | "bids" | "contracts" | "compliance" | "disputes" | "operations" | "messages" | "nudges" {
  switch (kind) {
    case "invoice_sent":
    case "invoice_paid":
    case "invoice_aging":
      return "money";
    case "bid_opportunity_matched":
    case "tender_amendment_published":
    case "tender_clarification_raised":
    case "tender_clarification_answered":
    case "bid_pack_submitted":
    case "bid_outcome_shortlisted":
    case "bid_outcome_won":
    case "bid_outcome_lost":
    case "tender_invitation_received":
    case "tender_invitation_reminder":
      return "bids";
    case "contract_offer_sent":
    case "contract_offer_responded":
    case "stage_change":
    /* v50.328 — framework agreements live with contracts. */
    case "framework_agreement_received":
    case "framework_agreement_signed":
    case "framework_agreement_declined":
    case "framework_agreement_terminated":
    case "framework_agreement_renewal_due":
      return "contracts";
    case "compliance_requested":
    case "compliance_fulfilled":
    case "compliance_expiring":
      return "compliance";
    case "dispute_filed":
    case "dispute_updated":
    case "milestone_evidence_submitted":
      return "disputes";
    case "message":
      return "messages";
    case "rating":
    case "rating_due":
      return "nudges";
    default:
      return "operations";
  }
}
